import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { safeAuthFetch } from '../../utils/safeFetch';
import { API_BASE_URL } from '../../config/api';
import { posthog } from '../../main';
import { SCENARIO_CONTEXT_LABELS } from '../../constants/scenarioContexts';
import { fetchDiscoveryShortlist } from '../../lib/discovery/discoveryApi';
import { startGuestRun } from '../../lib/discovery/startGuestRun';
import { guestStartErrorMessage } from '../../lib/discovery/guestStartErrorMessage';
import {
  CHOOSER_Q1_OPTIONS,
  CHOOSER_Q2_OPTIONS,
  CHOOSER_Q3_OPTIONS,
  reorderByIntent,
} from './chooserQuestions';
import ScenarioCard from './ScenarioCard';
import CommitmentSheetOverlay from './CommitmentSheetOverlay';
import './GuestChooser.css';

/**
 * The mountable #1922 chooser flow. The page route and learner-home sheet
 * both render this component so the questions cannot drift into two copies.
 *
 * `isOpen` hides the rendered sheet without unmounting this component. That
 * is load-bearing for #2495: dismissing and reopening must preserve the
 * learner's current question and answers rather than silently restarting.
 * It only parameterises the existing shortlist presentation; it never adds
 * a second recommender (#1768).
 */
function GuestChooser({ isOpen = true, presentation = 'page', onDismiss }) {
  const navigate = useNavigate();
  const { isAuthenticated, user, loginAsGuest } = useAuth();

  const [step, setStep] = useState('q1');
  const [context, setContext] = useState(null);
  const [intent, setIntent] = useState(null);
  const [mode, setMode] = useState(null);
  const [cards, setCards] = useState([]);
  const [isLoadingOutcome, setIsLoadingOutcome] = useState(false);
  const [outcomeError, setOutcomeError] = useState(false);
  const [committingScenario, setCommittingScenario] = useState(null);
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState(null);

  const exitChooser = useCallback(() => {
    if (onDismiss) {
      onDismiss();
      return;
    }
    navigate('/discover');
  }, [navigate, onDismiss]);

  const handleSkipToList = useCallback(() => {
    posthog?.capture?.('chooser_skipped_to_list');
    exitChooser();
  }, [exitChooser]);

  const handleQ1 = useCallback((option) => {
    posthog?.capture?.('chooser_question_answered', { question: 'q1', answer: option.context });
    setContext(option.context);
    setStep('q2');
  }, []);

  const handleQ2 = useCallback((option) => {
    posthog?.capture?.('chooser_question_answered', { question: 'q2', answer: option ? option.intent : 'skipped' });
    setIntent(option ? option.intent : null);
    setStep('q3');
  }, []);

  const handleQ3 = useCallback((option, currentContext, currentIntent) => {
    const chosenMode = option ? option.mode : null;
    posthog?.capture?.('chooser_question_answered', { question: 'q3', answer: chosenMode || 'skipped' });
    setMode(chosenMode);
    posthog?.capture?.('chooser_completed', { context: currentContext, intent: currentIntent, mode: chosenMode });
    setStep('outcome');
  }, []);

  // Closes only the nested commitment sheet (scrim click, "Pick a different
  // scenario", and Escape while it is open all land here).
  const dismissCommitment = useCallback(() => {
    setCommittingScenario(null);
    setStartError(null);
  }, []);

  // Standard modal-dialog keyboard behaviour: Escape dismisses the sheet.
  // Only wired for the sheet presentation — the page presentation has no
  // overlay to dismiss, Escape there would just be an unexpected no-op.
  //
  // #3475 Q2994-2: the nested commitment sheet has no keydown listener of its
  // own, so with it open Escape used to fall through and close the whole
  // chooser. The innermost layer now takes the key first. `committingScenario`
  // is in the deps (not read through a ref) so the listener is re-bound on
  // every open/close and never sees a stale value. Parity with the scrim
  // click: it is not gated on `isStarting`, exactly like the scrim.
  useEffect(() => {
    if (!isOpen || presentation !== 'sheet' || !onDismiss) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (committingScenario) {
        dismissCommitment();
        return;
      }
      onDismiss();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, presentation, onDismiss, committingScenario, dismissCommitment]);

  useEffect(() => {
    if (step !== 'outcome' || !context) return;
    let cancelled = false;
    setIsLoadingOutcome(true);
    setOutcomeError(false);
    fetchDiscoveryShortlist(context)
      .then((scenarios) => {
        if (!cancelled) setCards(reorderByIntent(scenarios, intent));
      })
      .catch(() => { if (!cancelled) setOutcomeError(true); })
      .finally(() => { if (!cancelled) setIsLoadingOutcome(false); });
    return () => { cancelled = true; };
  }, [step, context, intent]);

  const impressionKeyRef = useRef(null);
  // Bumped by Start over; a start that began under an older value is stale.
  const startGenerationRef = useRef(0);

  // #2495 fix-forward: GuestChooser now stays mounted across sheet dismissals
  // so in-progress answers survive (the #1862 anti-pattern this issue exists
  // to fix). That same persistence otherwise traps a learner who reaches the
  // outcome screen on a `sheet` presentation: closing and reopening always
  // re-shows the frozen outcome, with no in-sheet path back to q1 (the
  // "page" presentation still has its permanent "← Contexts" header link,
  // but a fresh route mount there made this moot before extraction).
  const handleStartOver = useCallback(() => {
    posthog?.capture?.('chooser_restarted');
    setStep('q1');
    setContext(null);
    setIntent(null);
    setMode(null);
    setCards([]);
    setOutcomeError(false);
    // #3475 Q2994-1: the component stays mounted across Start over, and both the
    // start-error paragraph and the commitment overlay render outside the step
    // blocks, so a leftover commitment / in-flight start / start error from the
    // previous attempt would resurface on the fresh q1 screen.
    setCommittingScenario(null);
    setIsStarting(false);
    setStartError(null);
    // The failure path of a start that was already in flight must not undo this
    // reset later (see handleStart's catch): invalidate it.
    startGenerationRef.current += 1;
    impressionKeyRef.current = null;
  }, []);

  useEffect(() => {
    if (step !== 'outcome' || isLoadingOutcome || outcomeError || cards.length === 0) return;
    const key = `${context}:${cards.map((card) => card.id).join(',')}`;
    if (impressionKeyRef.current === key) return;
    impressionKeyRef.current = key;
    posthog?.capture?.('discovery_shortlist_impression', {
      context,
      scenario_ids: cards.map((card) => card.id),
    });
  }, [step, isLoadingOutcome, outcomeError, cards, context]);

  const handleSelectCard = useCallback((card, position) => {
    posthog?.capture?.('discovery_card_tapped', {
      scenario_id: card.id,
      prep_required: !!card.prep_required,
      position,
    });
    setCommittingScenario(card);
  }, []);

  const handleStart = useCallback(async (modality) => {
    if (!committingScenario) return;
    const myGeneration = startGenerationRef.current;
    setIsStarting(true);
    setStartError(null);
    posthog?.capture?.('discovery_run_started', { scenario_id: committingScenario.id, modality });
    try {
      await startGuestRun({
        scenarioId: committingScenario.id,
        modality,
        isAuthenticated,
        user,
        loginAsGuest,
        safeAuthFetch,
        apiBaseUrl: API_BASE_URL,
        navigate,
      });
    } catch (err) {
      console.error('Failed to start guest session:', err);
      // Start over ran while this start was in flight: its reset already cleared
      // `isStarting`/`startError` for the fresh q1, so do not resurface this error.
      if (myGeneration !== startGenerationRef.current) return;
      setStartError(guestStartErrorMessage(err, 'Something went wrong starting that conversation. Try again.'));
      setIsStarting(false);
    }
  }, [committingScenario, isAuthenticated, user, loginAsGuest, navigate]);

  if (!isOpen) return null;

  const label = context ? SCENARIO_CONTEXT_LABELS[context] : null;
  const chooser = (
    <div className={`gd-chooser gd-chooser--${presentation}`} data-testid="gd-chooser">
      <header className="gd-chooser__header">
        {presentation === 'page' ? (
          <Link to="/discover" className="gd-page__back">← Contexts</Link>
        ) : (
          <>
            <span className="gd-chooser__eyebrow">Help you choose</span>
            <button type="button" className="gd-chooser__close" onClick={onDismiss} aria-label="Close chooser">×</button>
          </>
        )}
      </header>

      {step !== 'outcome' && (
        <button type="button" className="gd-chooser__escape" onClick={handleSkipToList} data-testid="gd-chooser-escape">
          Just show me the list
        </button>
      )}

      {step === 'q1' && (
        <div className="gd-chooser__question" data-testid="gd-chooser-q1">
          <h1 className="gd-page__title">Who&apos;s the conversation with?</h1>
          <div className="gd-chooser__options" role="list">
            {CHOOSER_Q1_OPTIONS.map((option) => (
              <button key={option.context} type="button" role="listitem" className="gd-chooser__option" onClick={() => handleQ1(option)}>
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 'q2' && (
        <div className="gd-chooser__question" data-testid="gd-chooser-q2">
          <h1 className="gd-page__title">What are you trying to do?</h1>
          <div className="gd-chooser__options" role="list">
            {CHOOSER_Q2_OPTIONS.map((option) => (
              <button key={option.intent} type="button" role="listitem" className="gd-chooser__option" onClick={() => handleQ2(option)}>
                {option.label}
              </button>
            ))}
          </div>
          <button type="button" className="gd-chooser__skip" onClick={() => handleQ2(null)}>Skip this question</button>
        </div>
      )}

      {step === 'q3' && (
        <div className="gd-chooser__question" data-testid="gd-chooser-q3">
          <h1 className="gd-page__title">Talk it out loud, or type it?</h1>
          <div className="gd-chooser__options" role="list">
            {CHOOSER_Q3_OPTIONS.map((option) => (
              <button key={option.mode} type="button" role="listitem" className="gd-chooser__option" onClick={() => handleQ3(option, context, intent)}>
                {option.label}
              </button>
            ))}
          </div>
          <button type="button" className="gd-chooser__skip" onClick={() => handleQ3(null, context, intent)}>Skip this question</button>
        </div>
      )}

      {step === 'outcome' && (
        <div data-testid="gd-chooser-outcome">
          <h1 className="gd-page__title">{label}</h1>
          <p className="gd-page__subtitle">Picked for you from what you told us.</p>
          {isLoadingOutcome && <p className="gd-page__status">Loading scenarios…</p>}
          {!isLoadingOutcome && outcomeError && <p className="gd-page__status gd-page__status--error">Couldn&apos;t load this shortlist right now.</p>}
          {!isLoadingOutcome && !outcomeError && cards.length === 0 && <p className="gd-page__status">No scenarios ready in this context yet.</p>}
          {!isLoadingOutcome && !outcomeError && cards.length > 0 && (
            <div className="gd-chooser__cards">
              {cards.map((card, index) => <ScenarioCard key={card.id} card={card} onSelect={() => handleSelectCard(card, index)} />)}
            </div>
          )}
          {!isLoadingOutcome && (
            <button type="button" className="gd-chooser__escape" onClick={handleStartOver} data-testid="gd-chooser-start-over">
              Start over
            </button>
          )}
        </div>
      )}

      {startError && <p className="gd-page__status gd-page__status--error">{startError}</p>}
      <CommitmentSheetOverlay
        scenario={committingScenario}
        isStarting={isStarting}
        onDismiss={dismissCommitment}
        onStart={handleStart}
        initialModality={mode}
      />
    </div>
  );

  if (presentation === 'sheet') {
    return (
      <div className="gd-chooser-overlay" data-testid="gd-chooser-overlay" role="dialog" aria-modal="true" aria-label="Help you choose" onClick={onDismiss}>
        <div className="gd-chooser-overlay__sheet" onClick={(event) => event.stopPropagation()}>{chooser}</div>
      </div>
    );
  }

  return chooser;
}

GuestChooser.propTypes = {
  isOpen: PropTypes.bool,
  presentation: PropTypes.oneOf(['page', 'sheet']),
  onDismiss: PropTypes.func,
};

export default GuestChooser;
